import { useState, useCallback } from "react";
import {
  ChecklistTemplateEntity,
  ChecklistTemplateItemEntity,
} from "../../domain/entities/ChecklistTemplateEntity";
import { ChecklistTemplateRepositoryImpl } from "../../data/repositories/ChecklistTemplateRepositoryImpl";
import {
  GetTemplateUseCase,
  UpdateTemplateUseCase,
  GetTemplateItemsUseCase,
  GetTemplateItemsGroupedUseCase,
} from "../../domain/use-cases/checklist-template";
import { ChecklistTemplateRepository } from "../../domain/repositories/ChecklistTemplateRepository";

// Create repository instance (singleton)
const templateRepository = new ChecklistTemplateRepositoryImpl();

// Create use cases
const getTemplateUseCase = new GetTemplateUseCase(templateRepository);
const updateTemplateUseCase = new UpdateTemplateUseCase(templateRepository);
const getTemplateItemsUseCase = new GetTemplateItemsUseCase(templateRepository);
const getTemplateItemsGroupedUseCase = new GetTemplateItemsGroupedUseCase(
  templateRepository
);

export interface UseChecklistTemplateReturn {
  template: ChecklistTemplateEntity | null;
  items: ChecklistTemplateItemEntity[];
  itemsByArea: Record<string, ChecklistTemplateItemEntity[]>;
  loading: boolean;
  error: Error | null;
  fetchTemplate: () => Promise<void>;
  fetchItems: () => Promise<void>;
  updateTemplate: (
    data: Partial<ChecklistTemplateEntity> & {
      items?: ChecklistTemplateItemEntity[];
    }
  ) => Promise<ChecklistTemplateEntity>;
  deleteTemplateItem: (itemId: number, deleteFromCurrentMonth?: boolean) => Promise<void>;
  addTemplateItem: (payload: {
    template_id: number;
    accounting_area: string;
    title: string;
    description?: string;
    task_guidance: string[];
    accepted_proof_types: string[];
    requires_proof?: boolean;
    order_index?: number;
    display_order?: number;
    priority?: string;
    link_url?: string;
    link_text?: string;
    system_info_query?: string;
    status?: string;
  }) => Promise<{
    item_id: number;
    added_to_current_month_checklist: boolean;
  }>;
  updateTemplateItem: (
    itemId: number,
    payload: {
      accounting_area?: string;
      title?: string;
      description?: string;
      task_guidance?: string[];
      accepted_proof_types?: string[];
      requires_proof?: boolean;
      order_index?: number;
      display_order?: number;
      priority?: string;
      link_url?: string;
      link_text?: string;
      system_info_query?: string;
      status?: string;
    }
  ) => Promise<{
    current_month_sync: { applied: boolean; skipped_reason: string | null };
  }>;
}

export const useChecklistTemplate = (): UseChecklistTemplateReturn => {
  const [template, setTemplate] = useState<ChecklistTemplateEntity | null>(
    null
  );
  const [items, setItems] = useState<ChecklistTemplateItemEntity[]>([]);
  const [itemsByArea, setItemsByArea] = useState<
    Record<string, ChecklistTemplateItemEntity[]>
  >({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchTemplate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const templateData = await getTemplateUseCase.execute();
      setTemplate(templateData); // Can be null if no template exists
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error("Failed to fetch template");
      setError(error);
      console.error("Failed to fetch template:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const currentTemplate = template || (await getTemplateUseCase.execute());
      if (!currentTemplate) {
        setItems([]); // No template, so no items
        setItemsByArea({});
        return;
      }

      // Use use cases to fetch items
      const [itemsList, groupedItems] = await Promise.all([
        getTemplateItemsUseCase.execute(currentTemplate.id),
        getTemplateItemsGroupedUseCase.execute(currentTemplate.id),
      ]);

      setItems(itemsList);
      setItemsByArea(groupedItems);
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error("Failed to fetch template items");
      setError(error);
      console.error("Failed to fetch template items:", error);
    } finally {
      setLoading(false);
    }
  }, [template]);

  const updateTemplateWithItems = useCallback(
    async (
      data: Partial<ChecklistTemplateEntity> & {
        items: ChecklistTemplateItemEntity[];
      }
    ): Promise<ChecklistTemplateEntity> => {
      try {
        setError(null);
        if (!template) {
          throw new Error("Template not loaded");
        }
        
        // Use the repository method that returns both template and items from response
        const result = await (templateRepository as any).updateTemplateWithItemsResponse(
          template.id,
          data
        );
        
        setTemplate(result.template);
        setItems(result.items);
        setItemsByArea(result.itemsByArea);
        
        return result.template;
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to update template")
        );
        throw err;
      }
    },
    [template]
  );

  const updateTemplate = useCallback(
    async (
      data: Partial<ChecklistTemplateEntity> & {
        items?: ChecklistTemplateItemEntity[];
      }
    ): Promise<ChecklistTemplateEntity> => {
      try {
        setError(null);
        if (!template) {
          throw new Error("Template not loaded");
        }
        // If items are provided, use the new update method
        if (data.items) {
          return await updateTemplateWithItems(
            data as Partial<ChecklistTemplateEntity> & {
              items: ChecklistTemplateItemEntity[];
            }
          );
        }
        // Otherwise, update template only
        const updatedTemplate = await updateTemplateUseCase.execute(
          template.id,
          data
        );
        setTemplate(updatedTemplate);
        return updatedTemplate;
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to update template")
        );
        throw err;
      }
    },
    [template, updateTemplateWithItems]
  );

  const deleteTemplateItemHandler = useCallback(
    async (itemId: number, deleteFromCurrentMonth: boolean = false): Promise<void> => {
      try {
        setError(null);
        await templateRepository.deleteTemplateItem(itemId, deleteFromCurrentMonth);
        // Remove item locally from state
        setItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
        // Update itemsByArea
        setItemsByArea((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((area) => {
            updated[area] = updated[area].filter((item) => item.id !== itemId);
            if (updated[area].length === 0) {
              delete updated[area];
            }
          });
          return updated;
        });
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to delete template item")
        );
        throw err;
      }
    },
    []
  );

  const addTemplateItemHandler = useCallback(
    async (payload: Parameters<UseChecklistTemplateReturn["addTemplateItem"]>[0]) => {
      try {
        setError(null);
        const result = await templateRepository.addTemplateItem(payload);
        await fetchItems();
        return result;
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to add template item")
        );
        throw err;
      }
    },
    [fetchItems]
  );

  const updateTemplateItemHandler = useCallback(
    async (
      itemId: number,
      payload: Parameters<UseChecklistTemplateReturn["updateTemplateItem"]>[1]
    ) => {
      try {
        setError(null);
        const result = await templateRepository.updateTemplateItem(itemId, payload);
        await fetchItems();
        return result;
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to update template item")
        );
        throw err;
      }
    },
    [fetchItems]
  );

  return {
    template,
    items,
    itemsByArea,
    loading,
    error,
    fetchTemplate,
    fetchItems,
    updateTemplate,
    deleteTemplateItem: deleteTemplateItemHandler,
    addTemplateItem: addTemplateItemHandler,
    updateTemplateItem: updateTemplateItemHandler,
  };
};
